/**
 * ReactiveProperty - A BehaviorSubject-like observable property
 *
 * This class provides:
 * - Direct value access (addressable)
 * - Automatic notifications to subscribers (auto-update)
 * - Works in both React and Potree (bindable)
 *
 * Similar to RxJS BehaviorSubject but without the dependency.
 */

export type Subscription = {
  unsubscribe: () => void;
};

export type PropertyListener<T> = (value: T) => void;

/**
 * A reactive property that holds a current value and notifies subscribers of changes.
 * Works like RxJS BehaviorSubject but is framework-agnostic.
 */
export class ReactiveProperty<T> {
  private _value: T;
  private listeners = new Set<PropertyListener<T>>();
  private _destroyed = false;

  constructor(initialValue: T) {
    this._value = initialValue;
  }

  /**
   * Get the current value (synchronous, direct access)
   */
  get value(): T {
    if (this._destroyed) {
      console.warn('Accessing value of destroyed ReactiveProperty');
    }
    return this._value;
  }

  /**
   * Set a new value and notify all subscribers
   */
  set value(newValue: T) {
    if (this._destroyed) {
      console.warn('Setting value on destroyed ReactiveProperty');
      return;
    }

    if (this._value !== newValue) {
      this._value = newValue;
      this.notify();
    }
  }

  /**
   * Update value without comparison (forces notification even if value is same)
   */
  next(newValue: T): void {
    if (this._destroyed) {
      console.warn('Calling next() on destroyed ReactiveProperty');
      return;
    }

    this._value = newValue;
    this.notify();
  }

  /**
   * Subscribe to value changes. The callback is called immediately with the current value.
   * @returns Subscription object with unsubscribe method
   */
  subscribe(callback: PropertyListener<T>): Subscription {
    if (this._destroyed) {
      console.warn('Subscribing to destroyed ReactiveProperty');
      return {
        unsubscribe: () => {
          /* no-op: property is destroyed */
        },
      };
    }

    this.listeners.add(callback);

    // Immediately call with current value (BehaviorSubject behavior)
    callback(this._value);

    return {
      unsubscribe: () => {
        this.listeners.delete(callback);
      },
    };
  }

  /**
   * Subscribe to value changes without getting the initial value
   * (only future changes)
   */
  subscribeToChanges(callback: PropertyListener<T>): Subscription {
    if (this._destroyed) {
      console.warn('Subscribing to destroyed ReactiveProperty');
      return {
        unsubscribe: () => {
          /* no-op: property is destroyed */
        },
      };
    }

    this.listeners.add(callback);

    return {
      unsubscribe: () => {
        this.listeners.delete(callback);
      },
    };
  }

  /**
   * Get number of active subscribers
   */
  get subscriberCount(): number {
    return this.listeners.size;
  }

  /**
   * Notify all subscribers of the current value
   */
  private notify(): void {
    // Create a copy to avoid issues if listeners modify the set during iteration
    const listenersCopy = Array.from(this.listeners);

    for (const listener of listenersCopy) {
      try {
        listener(this._value);
      } catch (error) {
        console.error('Error in ReactiveProperty listener:', error);
      }
    }
  }

  /**
   * Remove all subscribers and mark as destroyed
   */
  destroy(): void {
    this.listeners.clear();
    this._destroyed = true;
  }

  /**
   * Check if this property has been destroyed
   */
  get isDestroyed(): boolean {
    return this._destroyed;
  }
}
